import { authAPI, prefetchRead } from './api';

// Warm navigation data only; never execute reports, agents, or indexing jobs.
export async function prefetchDestination(path, caps, isCurrent = () => true, { allDomains = false, beforeRequest = async () => {} } = {}) {
  const warm = async (url, config) => {
    await beforeRequest();
    if (!isCurrent()) throw new Error('Prefetch cancelled');
    return prefetchRead(url, config);
  };
  const sequence = async requests => {
    const results = [];
    for (const [url, config] of requests) {
      try { results.push(await warm(url, config)); } catch (error) { results.push(undefined); if (!isCurrent()) throw error; }
    }
    return results;
  };
  if (path === '/dashboards') return warm('/dashboards');
  if (path === '/account') return warm('/chatgpt/account');
  if (path === '/subscription') {
    const settings = await warm('/company/settings');
    if (!allDomains) return;
    const permissions = settings.data.permissions || caps?.permissions || [];
    if (permissions.includes('audit.read')) await sequence([['/company/audit']]);
    if (permissions.includes('usage.read')) {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      await sequence([['/company/usage', { params: { start: start.toISOString(), end: end.toISOString() } }]]);
    }
    return;
  }
  if (path === '/chat') return sequence([['/auth/me'], ['/sessions']]);
  if (path === '/reports') {
    const reports = await warm('/reports', { params: { include_deleted: false } });
    await sequence([
      ['/reports/capabilities'],
      ['/domains', { params: { manage: false, include_archived: true, include_counts: false } }],
    ]);
    if (allDomains) await sequence(reports.data.map(report => [`/reports/${report.id}`]));
    return;
  }
  if (path === '/domains') {
    const user = authAPI.getCurrentUser();
    const manage = caps ? caps.permissions.includes('domains.manage') : String(user?.access_level || user?.role || '').toLowerCase() === 'admin';
    const domains = await warm('/domains', { params: { manage, include_archived: true } });
    if (allDomains && manage) for (const domain of domains.data.filter(item => item.is_active)) {
      await sequence([
        ['/data-sources', { params: { domain: domain.key } }],
        [`/domains/${domain.id}/members`],
        ...(!caps || caps.permissions.includes('domains.sender.manage') ? [[`/reports/sender/${domain.key}`]] : []),
      ]);
    }
    return;
  }
  if (!['/knowledge', '/agent-connections'].includes(path)) return;
  const domains = await warm('/domains', { params: { manage: false, include_archived: true } });
  const activeDomains = domains.data.filter(item => item.is_active);
  const selected = allDomains ? activeDomains : activeDomains.slice(0, 1);
  if (!isCurrent()) throw new Error('Prefetch cancelled');
  if (!selected.length) return;
  await sequence([[path === '/agent-connections' ? '/chatgpt/account' : '/auth/me']]);
  const csvEnabled = !caps || (caps.features?.['knowledge.csv_uploads'] ?? caps.features?.knowledge_base !== false);
  for (const { key: domain } of selected) {
    const config = { params: { domain } };
    const results = await sequence(path === '/agent-connections' ? [
      ['/agent-connections', config], ['/model-connections', config],
    ] : [
      ['/knowledge', config], ['/data-sources', config],
      ...(csvEnabled ? [['/knowledge/uploads', config]] : []),
    ]);
    if (allDomains && path === '/agent-connections') {
      await sequence((results[0]?.data?.agents || []).map(agent => [`/agent-connections/${domain}/${agent.agent_key}/history`]));
    }
  }
}
